import { useEffect, useRef, useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { devicesApi } from '@/lib/api'
import { Smartphone, Wifi, MapPin, Battery, RefreshCw, Radio, Trash2 } from 'lucide-react'
import { formatDateTime } from '@/lib/utils'

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000'

// Parse datetime from backend (no tz suffix) as UTC
function parseUtc(dt: string | null): number | null {
  if (!dt) return null
  const s = dt.endsWith('Z') || dt.includes('+') ? dt : dt + 'Z'
  const ms = new Date(s).getTime()
  return isNaN(ms) ? null : ms
}

function isRecent(dt: string | null): boolean {
  const ms = parseUtc(dt)
  if (!ms) return false
  return Date.now() - ms < 5 * 60 * 1000 // online = heartbeat within 5 min
}

function timeAgo(dt: string | null): string {
  const ms = parseUtc(dt)
  if (!ms) return '—'
  const s = Math.floor((Date.now() - ms) / 1000)
  if (s < 60) return `${s}s atrás`
  if (s < 3600) return `${Math.floor(s / 60)}min atrás`
  if (s < 86400) return `${Math.floor(s / 3600)}h atrás`
  return `${Math.floor(s / 86400)}d atrás`
}

interface WsHeartbeat {
  event: 'heartbeat'
  device_id: number
  last_seen_at: string
  battery_level: number | null
  latitude: string | null
  longitude: string | null
  network_type: string | null
  ip_address: string | null
}

export default function DevicesPage() {
  const queryClient = useQueryClient()
  const { data, isLoading, refetch } = useQuery({
    queryKey: ['devices'],
    queryFn: () => devicesApi.list({ page: 1, page_size: 100 }),
    refetchInterval: 30000,
  })

  const deleteMutation = useMutation({
    mutationFn: (id: number) => devicesApi.delete(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['devices'] }),
  })

  const handleDelete = (id: number, name: string) => {
    if (!window.confirm(`Remover dispositivo "${name}"?`)) return
    deleteMutation.mutate(id)
  }

  // WS live patches: device_id → partial device overrides
  const [liveData, setLiveData] = useState<Record<number, Partial<WsHeartbeat>>>({})
  const [wsConnected, setWsConnected] = useState(false)
  const wsRef = useRef<WebSocket | null>(null)

  useEffect(() => {
    const token = localStorage.getItem('access_token')
    if (!token) return

    const wsBase = API_URL.replace(/^http/, 'ws')
    let ws: WebSocket
    let reconnectTimer: ReturnType<typeof setTimeout>
    let pingTimer: ReturnType<typeof setInterval>

    const connect = () => {
      try {
        ws = new WebSocket(`${wsBase}/api/devices/ws?token=${encodeURIComponent(token)}`)
        wsRef.current = ws

        ws.onopen = () => {
          setWsConnected(true)
          // send periodic pings to keep the connection alive
          pingTimer = setInterval(() => {
            if (ws.readyState === WebSocket.OPEN) ws.send('ping')
          }, 30000)
        }

        ws.onmessage = (e) => {
          try {
            const msg: WsHeartbeat = JSON.parse(e.data)
            if (msg.event === 'heartbeat') {
              setLiveData(prev => ({
                ...prev,
                [msg.device_id]: {
                  last_seen_at: msg.last_seen_at,
                  battery_level: msg.battery_level,
                  latitude: msg.latitude,
                  longitude: msg.longitude,
                  network_type: msg.network_type,
                  ip_address: msg.ip_address,
                },
              }))
            }
          } catch (_) {}
        }

        ws.onclose = () => {
          setWsConnected(false)
          clearInterval(pingTimer)
          reconnectTimer = setTimeout(connect, 5000)
        }

        ws.onerror = () => {
          ws.close()
        }
      } catch (_) {}
    }

    connect()
    return () => {
      clearTimeout(reconnectTimer)
      clearInterval(pingTimer)
      ws?.close()
    }
  }, [])

  const baseDevices: any[] = data?.data?.items ?? []

  // Merge live WS updates into query data
  const devices = baseDevices.map((d: any) => ({
    ...d,
    ...(liveData[d.id] ?? {}),
  }))

  const onlineCount = devices.filter((d: any) => isRecent(d.last_seen_at)).length

  return (
    <div>
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Dispositivos</h1>
          <p className="mt-1 text-sm text-gray-600 flex items-center gap-2">
            Atualiza automaticamente a cada 30s
            <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full ${
              wsConnected ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'
            }`}>
              <Radio className="h-3 w-3" />
              {wsConnected ? 'Tempo real ativo' : 'A reconectar…'}
            </span>
          </p>
        </div>
        <button
          onClick={() => refetch()}
          className="flex items-center gap-2 rounded-lg border px-3 py-2 text-sm text-gray-600 hover:bg-gray-50"
        >
          <RefreshCw className="h-4 w-4" />
          Atualizar
        </button>
      </div>

      <div className="mt-6 card overflow-x-auto">
        {isLoading ? (
          <div className="flex justify-center py-12">
            <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary-600 border-t-transparent" />
          </div>
        ) : devices.length === 0 ? (
          <div className="py-16 text-center text-gray-400">
            <Smartphone className="mx-auto mb-3 h-10 w-10 opacity-40" />
            <p>Nenhum dispositivo registado ainda.</p>
            <p className="mt-1 text-sm">Os dispositivos aparecem aqui após o primeiro login na app Android.</p>
          </div>
        ) : (
          <table className="table min-w-full">
            <thead>
              <tr>
                <th>Estado</th>
                <th>Dispositivo</th>
                <th>Sistema</th>
                <th>Versão App</th>
                <th>Rede</th>
                <th>IP</th>
                <th>GPS</th>
                <th>Bateria</th>
                <th>Visto há</th>
                <th>Registado</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {devices.map((device: any) => {
                const online = isRecent(device.last_seen_at)
                return (
                  <tr key={device.id}>
                    {/* Estado */}
                    <td>
                      <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${
                        online ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'
                      }`}>
                        <span className={`h-1.5 w-1.5 rounded-full ${online ? 'bg-green-500 animate-pulse' : 'bg-gray-400'}`} />
                        {online ? 'Online' : 'Offline'}
                      </span>
                    </td>

                    {/* Dispositivo */}
                    <td>
                      <div className="flex items-center gap-2">
                        <Smartphone className="h-5 w-5 shrink-0 text-gray-400" />
                        <div>
                          <p className="font-medium text-gray-900">{device.device_name || '—'}</p>
                          <p className="text-xs text-gray-400 font-mono">{device.device_identifier?.slice(0, 16)}…</p>
                        </div>
                      </div>
                    </td>

                    {/* Sistema */}
                    <td>
                      <p className="text-sm">{device.device_model || '—'}</p>
                      <p className="text-xs text-gray-400">{device.os_version || '—'}</p>
                    </td>

                    {/* App version */}
                    <td className="font-mono text-sm">{device.app_version || '—'}</td>

                    {/* Rede */}
                    <td>
                      {device.network_type ? (
                        <span className="flex items-center gap-1 text-sm">
                          <Wifi className="h-3.5 w-3.5 text-blue-500" />
                          {device.network_type}
                        </span>
                      ) : '—'}
                    </td>

                    {/* IP */}
                    <td className="font-mono text-xs">{device.ip_address || '—'}</td>

                    {/* GPS */}
                    <td>
                      {device.latitude && device.longitude ? (
                        <a
                          href={`https://maps.google.com/?q=${device.latitude},${device.longitude}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-1 text-xs text-blue-600 hover:underline"
                        >
                          <MapPin className="h-3 w-3" />
                          {parseFloat(device.latitude).toFixed(4)}, {parseFloat(device.longitude).toFixed(4)}
                        </a>
                      ) : (
                        <span className="text-xs text-gray-400">Sem GPS</span>
                      )}
                    </td>

                    {/* Bateria */}
                    <td>
                      {device.battery_level != null ? (
                        <span className={`flex items-center gap-1 text-sm font-medium ${
                          device.battery_level < 20 ? 'text-red-500' :
                          device.battery_level < 50 ? 'text-amber-500' : 'text-green-600'
                        }`}>
                          <Battery className="h-3.5 w-3.5" />
                          {device.battery_level}%
                        </span>
                      ) : '—'}
                    </td>

                    {/* Visto há */}
                    <td>
                      <p className={`text-xs font-medium ${online ? 'text-green-600' : 'text-gray-500'}`}>
                        {timeAgo(device.last_seen_at)}
                      </p>
                      <p className="text-xs text-gray-400">{formatDateTime(device.last_seen_at)}</p>
                    </td>

                    {/* Registado */}
                    <td className="text-xs text-gray-400">{formatDateTime(device.created_at)}</td>

                    {/* Eliminar (só offline) */}
                    <td>
                      {!online && (
                        <button
                          title="Remover dispositivo"
                          onClick={() => handleDelete(device.id, device.device_name || 'Dispositivo')}
                          disabled={deleteMutation.isPending}
                          className="p-1 rounded text-gray-400 hover:text-red-500 hover:bg-red-50 transition-colors disabled:opacity-40"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      )}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        )}
      </div>

      <p className="mt-3 text-xs text-gray-400">
        Total: {devices.length} dispositivo{devices.length !== 1 ? 's' : ''} •
        Online: {onlineCount}
      </p>
    </div>
  )
}
